import type { ReactNode } from 'react'
import type { XoxReaction } from '../../lib/reactions'

const X = 'var(--color-x)'
const O = 'var(--color-o)'
const LINE = 'var(--skater-line)'
const SHEEN = 'var(--ice-sheen)'
const FLAME = 'var(--ice-brass)'

/** Thick rounded X, the mark as a body. */
const XBody = () => (
  <path d="M9 9 L31 31 M31 9 L9 31" stroke={X} strokeWidth="7" strokeLinecap="round" />
)
/** Thick O ring, the mark as a face. */
const ORing = () => <circle cx="20" cy="22" r="12" fill="none" stroke={O} strokeWidth="6" />

/** Reaction stickers for Vanishing XOX: the marks themselves pulling faces (0..40 box). */
const STICKERS: Record<XoxReaction, ReactNode> = {
  lol: (
    <>
      <XBody />
      <circle cx="20" cy="20" r="7.5" fill={X} />
      <path
        d="M15.5 17.5 L18 19 M24.5 17.5 L22 19"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M15 21.5 Q20 27 25 21.5 Z" fill="white" />
      <path d="M17 24 Q20 26 23 24 Q20 27.5 17 24 Z" fill={X} />
      <path d="M30 14 C30 16.5 28.5 17.5 28 19 C27.5 17.5 26 16.5 26 14 Z" fill={O} />
    </>
  ),
  fire: (
    <>
      <path
        d="M20 2 C24 7 27 9 25 14 C29 12 30 8.5 30 8.5 C33 14 30 18 26 19 H14 C10 18 8 13 11 9 C12 12 14 13 16 12 C14 8 16 5 20 2 Z"
        fill={FLAME}
      />
      <path d="M20 7 C22 10 23 12 22 14.5 H17.5 C16.5 12 18 10 20 7 Z" fill={X} />
      <ORing />
      <circle cx="16.5" cy="21" r="1.6" fill={LINE} />
      <circle cx="23.5" cy="21" r="1.6" fill={LINE} />
      <path
        d="M16.5 26 Q20 28.5 23.5 26"
        fill="none"
        stroke={LINE}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </>
  ),
  wow: (
    <>
      <ORing />
      <circle cx="15.5" cy="20.5" r="3.6" fill="white" stroke={LINE} strokeWidth="1" />
      <circle cx="24.5" cy="20.5" r="3.6" fill="white" stroke={LINE} strokeWidth="1" />
      <circle cx="15.8" cy="21" r="1.8" fill={LINE} />
      <circle cx="24.8" cy="21" r="1.8" fill={LINE} />
      <circle cx="20" cy="27.5" r="2.3" fill="none" stroke={LINE} strokeWidth="1.5" />
      <path
        d="M5 8 L7.5 11 M10 4 L10.5 8 M35 8 L32.5 11 M30 4 L29.5 8"
        stroke={SHEEN}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </>
  ),
  clap: (
    <>
      <path d="M6 24 L15 16 M6 16 L15 24" stroke={X} strokeWidth="5" strokeLinecap="round" />
      <path d="M25 24 L34 16 M25 16 L34 24" stroke={X} strokeWidth="5" strokeLinecap="round" />
      <path
        d="M18 12 L20 8 M20 15 L22 11 M22 18 L24 15"
        stroke={FLAME}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M19 27 L20 31 M17 25 L16 29 M22 25 L23 29"
        stroke={FLAME}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M12 34 Q20 30 28 34"
        fill="none"
        stroke={LINE}
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity=".5"
      />
    </>
  ),
  cry: (
    <>
      <ORing />
      <path d="M14 19 L18 21 M26 19 L22 21" stroke={LINE} strokeWidth="1.6" strokeLinecap="round" />
      <path
        d="M16 28 Q18 25.5 20 28 Q22 25.5 24 28"
        fill="none"
        stroke={LINE}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M14.5 22 C13 27 12.5 31 14.5 36 C16.5 31 16 27 14.5 22 Z" fill={O} opacity=".85" />
      <path d="M25.5 22 C24 27 23.5 31 25.5 36 C27.5 31 27 27 25.5 22 Z" fill={O} opacity=".85" />
    </>
  ),
  think: (
    <>
      <XBody />
      <circle cx="20" cy="20" r="7" fill={X} />
      <path d="M15.5 17 L18.5 18" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M21.5 15.5 L24.5 17" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="17.3" cy="20" r="1.1" fill="white" />
      <circle cx="23" cy="19.5" r="1.1" fill="white" />
      <path d="M17.5 24 L22.5 23" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="30" cy="12" r="1.6" fill={SHEEN} />
      <circle cx="33" cy="8" r="2.4" fill={SHEEN} />
      <circle cx="37" cy="3.5" r="3" fill="white" stroke={LINE} strokeWidth=".8" />
      <text
        x="37"
        y="5.4"
        textAnchor="middle"
        fontSize="4.6"
        fontWeight="900"
        fontFamily="inherit"
        fill={LINE}
      >
        ?
      </text>
    </>
  ),
}

export function XoxSticker({ id, className = '' }: { id: XoxReaction; className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden>
      {STICKERS[id]}
    </svg>
  )
}
